import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { P1S } from '../settings/schema';

import { store, useAppStore, isOutOfBounds, objectBounds } from '../store/appStore';
import type { PlacedObject } from '../store/types';
import { previewStore } from '../preview/previewStore';
import { ToolpathRenderer } from './toolpaths';

function themeIsDark() {
  const t = store.getSnapshot().theme;
  if (t === 'dark') return true;
  if (t === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function makePeiTexture(dark: boolean): THREE.CanvasTexture {
  const size = 1024;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  // Cura-like plate: medium-grey margin, light inner field, fine thin grid
  ctx.fillStyle = dark ? '#4a4e56' : '#b5b8bd';
  ctx.fillRect(0, 0, size, size);
  const margin = Math.round(size * (12 / 256));
  ctx.fillStyle = dark ? '#32363e' : '#e4e6e9';
  ctx.fillRect(margin, margin, size - 2 * margin, size - 2 * margin);
  const cells = 16;
  const inner = size - 2 * margin;
  ctx.strokeStyle = dark ? 'rgba(0, 0, 0, 0.38)' : 'rgba(132, 136, 142, 0.55)';
  ctx.lineWidth = 1;
  for (let i = 0; i <= cells; i++) {
    const p = margin + (inner * i) / cells;
    ctx.beginPath();
    ctx.moveTo(p, margin);
    ctx.lineTo(p, size - margin);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(margin, p);
    ctx.lineTo(size - margin, p);
    ctx.stroke();
  }
  ctx.strokeStyle = dark ? 'rgba(0, 0, 0, 0.32)' : 'rgba(148, 152, 158, 0.35)';
  ctx.lineWidth = 1;
  ctx.strokeRect(margin + 0.5, margin + 0.5, inner - 1, inner - 1);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}

function makeBackdrop(dark: boolean): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 8;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  if (dark) {
    g.addColorStop(0, '#2a2f3c');
    g.addColorStop(1, '#16181f');
  } else {
    g.addColorStop(0, '#f7f8fa');
    g.addColorStop(1, '#d7dbe1');
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 8, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function makeSoftShadowTexture(): THREE.CanvasTexture {
  const size = 128;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, size * 0.06, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(0,0,0,0.22)');
  g.addColorStop(0.45, 'rgba(0,0,0,0.08)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.needsUpdate = true;
  return tex;
}

const SOFT_SHADOW_TEX = makeSoftShadowTexture();

/** sRGB CSS/hex → working-space Color (linear when ColorManagement is on). */
export function colorFromCss(raw: unknown, fallback = '#E8C547'): THREE.Color {
  let s = String(raw ?? fallback).trim() || fallback;
  if (s[0] !== '#' && !s.startsWith('rgb')) s = `#${s}`;
  const c = new THREE.Color();
  try {
    c.setStyle(s);
  } catch {
    c.setStyle(fallback);
  }
  if (!THREE.ColorManagement.enabled) c.convertSRGBToLinear();
  return c;
}

export function parseFilamentColor(raw: unknown): number {
  const s = String(raw ?? '#E8C547').trim();
  const hex = s.startsWith('#') ? s.slice(1) : s;
  const n = parseInt(hex.slice(0, 6), 16);
  return Number.isFinite(n) ? n : 0xe8c547;
}

function physicalOpts(extra: ConstructorParameters<typeof THREE.MeshPhysicalMaterial>[0] = {}) {
  return {
    metalness: 0,
    roughness: 0.35,
    clearcoat: 0.6,
    clearcoatRoughness: 0.18,
    envMapIntensity: 1.0,
    side: THREE.DoubleSide,
    ...extra,
  };
}

function meshFromObject(o: PlacedObject, colorCss: string, paint?: boolean): THREE.Mesh {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(o.positions, 3));
  geo.computeVertexNormals();

  let mat: THREE.Material;
  if (paint && o.paintSupports) {
    const colors = new Float32Array(o.positions.length);
    const base = colorFromCss(colorCss);
    const enforce = colorFromCss('#22c55e');
    const block = colorFromCss('#ef4444');
    for (let t = 0; t < o.paintSupports.length; t++) {
      const v = o.paintSupports[t];
      const c = v === 1 ? enforce : v === 2 ? block : base;
      for (let k = 0; k < 3; k++) {
        const i = (t * 3 + k) * 3;
        colors[i] = c.r;
        colors[i + 1] = c.g;
        colors[i + 2] = c.b;
      }
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    mat = new THREE.MeshPhysicalMaterial(physicalOpts({ vertexColors: true }));
  } else {
    mat = new THREE.MeshPhysicalMaterial(physicalOpts({ color: colorFromCss(colorCss) }));
  }

  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(o.position[0], o.position[2], o.position[1]);
  mesh.rotation.set(o.rotation[0], o.rotation[1], o.rotation[2]);
  mesh.scale.set(o.scale[0], o.scale[1], o.scale[2]);
  mesh.userData.objectId = o.id;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function applyOverhangColors(mesh: THREE.Mesh, angleDeg: number) {
  const geo = mesh.geometry as THREE.BufferGeometry;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const thresh = Math.cos((angleDeg * Math.PI) / 180);
  const up = new THREE.Vector3(0, 1, 0);
  const a = new THREE.Vector3(),
    b = new THREE.Vector3(),
    c = new THREE.Vector3(),
    n = new THREE.Vector3();
  const ok = colorFromCss('#60a5fa');
  const bad = colorFromCss('#f97316');
  for (let t = 0; t < pos.count; t += 3) {
    a.fromBufferAttribute(pos, t);
    b.fromBufferAttribute(pos, t + 1);
    c.fromBufferAttribute(pos, t + 2);
    n.crossVectors(b.clone().sub(a), c.clone().sub(a)).normalize();
    // Transform normal by mesh world (approx rotation only)
    n.applyEuler(mesh.rotation);
    const overhang = n.dot(up) < -thresh; // facing down past threshold
    const col = overhang ? bad : ok;
    for (let k = 0; k < 3; k++) {
      colors[(t + k) * 3] = col.r;
      colors[(t + k) * 3 + 1] = col.g;
      colors[(t + k) * 3 + 2] = col.b;
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  (mesh.material as THREE.MeshStandardMaterial).vertexColors = true;
  (mesh.material as THREE.MeshStandardMaterial).needsUpdate = true;
}

export interface PlateViewerHandle {
  captureThumbnail: () => Promise<Uint8Array | undefined>;
}

export function PlateViewer() {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const volRef = useRef<THREE.LineSegments | null>(null);
  const plateMatRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const bgTexRef = useRef<THREE.CanvasTexture | null>(null);
  const lightsRef = useRef<{
    hemi: THREE.HemisphereLight;
    key: THREE.DirectionalLight;
    fill: THREE.DirectionalLight;
  } | null>(null);
  const apiRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    controls: OrbitControls;
    objectGroup: THREE.Group;
    paths: ToolpathRenderer;
    rebuild: () => void;
  } | null>(null);

  const objects = useAppStore((s) => s.objects);
  const selectedId = useAppStore((s) => s.selectedId);
  const theme = useAppStore((s) => s.theme);
  const tool = useAppStore((s) => s.tool);
  const brushSize = useAppStore((s) => s.brushSize);
  const overhangHighlight = useAppStore((s) => s.overhangHighlight);
  const overhangAngle = useAppStore((s) => s.overhangAngle);
  const filamentColour = useAppStore((s) => s.settings.filament_colour);
  const viewMode = useAppStore((s) => s.viewMode);

  // Init three.js once
  useEffect(() => {
    const el = mountRef.current!;
    const scene = new THREE.Scene();
    const bgTex = makeBackdrop(themeIsDark());
    scene.background = bgTex;
    bgTexRef.current = bgTex;
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 2000);
    // Closer, lower three-quarter view so the plate fills the frame (Cura-like)
    camera.position.set(P1S.bedX * 0.78, P1S.bedZ * 0.38, P1S.bedY * 0.94);
    camera.lookAt(P1S.bedX / 2, 2, P1S.bedY / 2);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.toneMappingExposure = 1.15;
    el.appendChild(renderer.domElement);

    const pmrem = new THREE.PMREMGenerator(renderer);
    const envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envMap;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(P1S.bedX / 2, 6, P1S.bedY / 2);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    // Apple Pencil / stylus: treat as mouse
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };

    // Physically-correct lights (r155+): higher intensities + studio env for clearcoat
    const dark0 = themeIsDark();
    const hemi = new THREE.HemisphereLight(0xffffff, 0xcfcbc6, dark0 ? 0.9 : 1.15);
    scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, dark0 ? 4.2 : 5.6);
    key.position.set(P1S.bedX / 2 + 40, 380, P1S.bedY / 2 + 20);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.radius = 5;
    key.shadow.camera.left = -190;
    key.shadow.camera.right = 190;
    key.shadow.camera.top = 190;
    key.shadow.camera.bottom = -190;
    key.shadow.camera.near = 20;
    key.shadow.camera.far = 700;
    key.shadow.bias = -0.00035;
    key.shadow.normalBias = 0.35;
    key.target.position.set(P1S.bedX / 2, 0, P1S.bedY / 2);
    scene.add(key);
    scene.add(key.target);
    const fill = new THREE.DirectionalLight(0xe8eeff, dark0 ? 1.1 : 1.5);
    fill.position.set(P1S.bedX / 2 - 160, 120, P1S.bedY / 2 - 110);
    scene.add(fill);
    lightsRef.current = { hemi, key, fill };

    // Build plate
    const plateGeo = new THREE.PlaneGeometry(P1S.bedX, P1S.bedY);
    const plateMat = new THREE.MeshBasicMaterial({
      map: makePeiTexture(dark0),
      color: 0xffffff,
    });
    plateMatRef.current = plateMat;
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.rotation.x = -Math.PI / 2;
    plate.position.set(P1S.bedX / 2, 0, P1S.bedY / 2);
    plate.receiveShadow = true;
    scene.add(plate);

    // Volume wireframe — subtle translucent blue cage
    const vol = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(P1S.bedX, P1S.bedZ, P1S.bedY)),
      new THREE.LineBasicMaterial({ color: dark0 ? 0x6ea8ff : 0x6aa8f0, transparent: true, opacity: 0.32 })
    );
    vol.position.set(P1S.bedX / 2, P1S.bedZ / 2, P1S.bedY / 2);
    volRef.current = vol;
    scene.add(vol);

    const objectGroup = new THREE.Group();
    scene.add(objectGroup);
    const paths = new ToolpathRenderer();
    scene.add(paths.group);
    paths.group.visible = false;

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    function resize() {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
      paths.setSize(w, h);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    window.addEventListener('resize', resize);

    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      controls.update();
      renderer.render(scene, camera);
    };
    tick();

    function rebuild() {
      const s = store.getSnapshot();
      paths.group.visible = s.viewMode === 'preview';
      objectGroup.visible = s.viewMode !== 'preview';
      if (s.viewMode === 'preview') {
        const prev = previewStore.getSnapshot();
        paths.update(prev, parseFilamentColor(s.settings.filament_colour), Number(s.settings.outer_wall_line_width ?? 0.42) || 0.42);
      }
      while (objectGroup.children.length) {
        const ch = objectGroup.children.pop()!;
        (ch as THREE.Mesh).geometry?.dispose?.();
        const m = (ch as THREE.Mesh).material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m?.dispose?.();
      }
      for (const o of s.objects) {
        const selected = o.id === s.selectedId;
        const oob = isOutOfBounds(o);
        const color = oob ? '#ef4444' : String(s.settings.filament_colour ?? '#E8C547');
        const showPaint =
          s.tool.startsWith('paint') ||
          (o.paintSupports?.some((v) => v > 0) ?? false);
        const mesh = meshFromObject(o, color, showPaint && !s.overhangHighlight);
        if (s.overhangHighlight) applyOverhangColors(mesh, s.overhangAngle);
        if (selected && !s.overhangHighlight) {
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(mesh.geometry as THREE.BufferGeometry, 28),
            new THREE.LineBasicMaterial({ color: 0x00ae42, transparent: true, opacity: 0.9 })
          );
          edges.position.copy(mesh.position);
          edges.rotation.copy(mesh.rotation);
          edges.scale.copy(mesh.scale);
          objectGroup.add(edges);
        }
        const bb = objectBounds(o);
        const blob = new THREE.Mesh(
          new THREE.CircleGeometry(Math.max(bb.sx, bb.sz) * 0.85, 48),
          new THREE.MeshBasicMaterial({
            map: SOFT_SHADOW_TEX,
            transparent: true,
            opacity: themeIsDark() ? 0.85 : 0.7,
            depthWrite: false,
          })
        );
        blob.rotation.x = -Math.PI / 2;
        blob.position.set(o.position[0], 0.04, o.position[1]);
        blob.renderOrder = -1;
        objectGroup.add(blob);
        objectGroup.add(mesh);
      }
    }

    function ndcFromEvent(e: PointerEvent) {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    }

    function paintAt(e: PointerEvent) {
      const s = store.getSnapshot();
      if (!s.tool.startsWith('paint') || !s.selectedId) return;
      ndcFromEvent(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter(
        (c) => (c as THREE.Mesh).isMesh && (c as THREE.Mesh).userData.objectId === s.selectedId
      ) as THREE.Mesh[];
      const hits = raycaster.intersectObjects(meshes, false);
      if (!hits.length) return;
      const hit = hits[0];
      const faceIndex = hit.faceIndex ?? 0;
      const mesh = hit.object as THREE.Mesh;
      // Expand brush by nearby triangle centroids
      const geo = mesh.geometry as THREE.BufferGeometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute;
      const center = new THREE.Vector3();
      center.fromBufferAttribute(pos, faceIndex * 3);
      center.add(new THREE.Vector3().fromBufferAttribute(pos, faceIndex * 3 + 1));
      center.add(new THREE.Vector3().fromBufferAttribute(pos, faceIndex * 3 + 2));
      center.multiplyScalar(1 / 3);
      mesh.localToWorld(center);

      const radius = s.brushSize;
      const tris: number[] = [];
      const tmp = new THREE.Vector3();
      const triCount = pos.count / 3;
      for (let t = 0; t < triCount; t++) {
        tmp.fromBufferAttribute(pos, t * 3);
        tmp.add(new THREE.Vector3().fromBufferAttribute(pos, t * 3 + 1));
        tmp.add(new THREE.Vector3().fromBufferAttribute(pos, t * 3 + 2));
        tmp.multiplyScalar(1 / 3);
        mesh.localToWorld(tmp);
        if (tmp.distanceTo(center) <= radius) tris.push(t);
      }
      const value: 0 | 1 | 2 =
        s.tool === 'paint-enforce' ? 1 : s.tool === 'paint-block' ? 2 : 0;
      store.paintTriangles(s.selectedId, tris, value);
    }

    let painting = false;
    const onPointerDown = (e: PointerEvent) => {
      const s = store.getSnapshot();
      ndcFromEvent(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter((c) => (c as THREE.Mesh).isMesh) as THREE.Mesh[];
      const hits = raycaster.intersectObjects(meshes, false);

      if (s.tool.startsWith('paint')) {
        if (hits[0]?.object.userData.objectId) {
          store.select(hits[0].object.userData.objectId);
        }
        painting = true;
        controls.enabled = false;
        const label = s.tool === 'paint-enforce' ? 'Paint' : s.tool === 'paint-block' ? 'Block' : 'Erase';
        store.beginGesture(label);
        paintAt(e);
        e.preventDefault();
        return;
      }

      if (hits.length) {
        store.select(hits[0].object.userData.objectId);
      } else if (e.pointerType !== 'pen') {
        // tap empty → deselect on touch? keep selection
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!painting) return;
      paintAt(e);
    };
    const onPointerUp = () => {
      if (painting) store.endGesture();
      painting = false;
      controls.enabled = true;
    };

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    apiRef.current = { scene, camera, renderer, controls, objectGroup, paths, rebuild };
    rebuild();

    // Expose thumbnail helper on element
    (el as unknown as { __capture?: () => Promise<Uint8Array | undefined> }).__capture = async () => {
      renderer.render(scene, camera);
      const dataUrl = renderer.domElement.toDataURL('image/png');
      const res = await fetch(dataUrl);
      const buf = new Uint8Array(await res.arrayBuffer());
      return buf;
    };

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', resize);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      controls.dispose();
      scene.environment = null;
      envMap.dispose();
      pmrem.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, []);

  // Rebuild meshes when state changes
  useEffect(() => {
    apiRef.current?.rebuild();
  }, [objects, selectedId, tool, brushSize, overhangHighlight, overhangAngle, theme, filamentColour, viewMode]);

  useEffect(() => {
    const unsub = previewStore.subscribe(() => {
      const api = apiRef.current;
      if (!api) return;
      const s = store.getSnapshot();
      if (s.viewMode !== 'preview') {
        api.paths.group.visible = false;
        return;
      }
      api.paths.group.visible = true;
      api.paths.update(
        previewStore.getSnapshot(),
        parseFilamentColor(s.settings.filament_colour),
        Number(s.settings.outer_wall_line_width ?? 0.42) || 0.42
      );
    });
    return () => { unsub(); };
  }, []);

  // Drag move when tool=move
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    const { renderer, camera, controls, objectGroup } = api;
    if (tool !== 'move' && tool !== 'rotate' && tool !== 'scale') return;

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const hit = new THREE.Vector3();
    let dragging = false;
    let startBed: THREE.Vector3 | null = null;
    let startPos: [number, number, number] | null = null;

    const ndc = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const down = (e: PointerEvent) => {
      const s = store.getSnapshot();
      if (!s.selectedId) return;
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter(
        (c) => (c as THREE.Mesh).isMesh && (c as THREE.Mesh).userData.objectId === s.selectedId
      );
      if (!raycaster.intersectObjects(meshes as THREE.Object3D[], false).length) return;
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      dragging = true;
      controls.enabled = false;
      startBed = hit.clone();
      const o = s.objects.find((x) => x.id === s.selectedId)!;
      startPos = [...o.position] as [number, number, number];
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      if (!dragging || !startBed || !startPos) return;
      const s = store.getSnapshot();
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const dx = hit.x - startBed.x;
      const dz = hit.z - startBed.z;
      if (tool === 'move') {
        store.updateObject(s.selectedId!, {
          position: [startPos[0] + dx, startPos[1] + dz, startPos[2]],
        });
      } else if (tool === 'rotate') {
        const yaw = dx * 0.02;
        const o = s.objects.find((x) => x.id === s.selectedId)!;
        store.updateObject(s.selectedId!, {
          rotation: [o.rotation[0], startPos[0] /* misuse */ + yaw, o.rotation[2]],
        });
        // Fix: store start rot
      } else if (tool === 'scale') {
        const factor = 1 + dx * 0.01;
        const o = s.objects.find((x) => x.id === s.selectedId)!;
        const f = Math.max(0.05, factor);
        store.updateObject(s.selectedId!, {
          scale: [o.scale[0] * (f / (o.scale[0] || 1)), o.scale[1] * (f / (o.scale[0] || 1)), o.scale[2] * (f / (o.scale[0] || 1))],
        });
      }
    };
    // Better rotate/scale handlers
    let startRotY = 0;
    let startScale = 1;
    const down2 = (e: PointerEvent) => {
      const s = store.getSnapshot();
      if (!s.selectedId) return;
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter(
        (c) => (c as THREE.Mesh).isMesh && (c as THREE.Mesh).userData.objectId === s.selectedId
      );
      const hitMesh = raycaster.intersectObjects(meshes as THREE.Object3D[], false).length > 0;
      if (!hitMesh && tool === 'move') return;
      // rotate/scale: allow drag anywhere once an object is selected
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const o = s.objects.find((x) => x.id === s.selectedId);
      if (!o) return;
      dragging = true;
      controls.enabled = false;
      startBed = hit.clone();
      startPos = [...o.position] as [number, number, number];
      startRotY = o.rotation[1];
      startScale = o.scale[0];
      store.beginGesture(tool === 'move' ? 'Move' : tool === 'rotate' ? 'Rotate' : 'Scale');
      e.preventDefault();
    };
    const move2 = (e: PointerEvent) => {
      if (!dragging || !startBed || !startPos) return;
      const s = store.getSnapshot();
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const dx = hit.x - startBed.x;
      const dz = hit.z - startBed.z;
      if (tool === 'move') {
        store.updateObject(s.selectedId!, {
          position: [startPos[0] + dx, startPos[1] + dz, startPos[2]],
        });
      } else if (tool === 'rotate') {
        store.updateObject(s.selectedId!, {
          rotation: [
            store.getSnapshot().objects.find((x) => x.id === s.selectedId)!.rotation[0],
            startRotY + dx * 0.03,
            store.getSnapshot().objects.find((x) => x.id === s.selectedId)!.rotation[2],
          ],
        });
      } else if (tool === 'scale') {
        const f = Math.max(0.05, startScale * (1 + dx * 0.02));
        store.updateObject(s.selectedId!, { scale: [f, f, f] });
      }
    };
    const up = () => {
      if (dragging) store.endGesture();
      dragging = false;
      controls.enabled = true;
      startBed = null;
    };

    renderer.domElement.addEventListener('pointerdown', down2);
    renderer.domElement.addEventListener('pointermove', move2);
    window.addEventListener('pointerup', up);
    return () => {
      renderer.domElement.removeEventListener('pointerdown', down2);
      renderer.domElement.removeEventListener('pointermove', move2);
      window.removeEventListener('pointerup', up);
      // silence unused
      void down;
      void move;
    };
  }, [tool]);

  useEffect(() => {
    if (!sceneRef.current) return;
    const dark = themeIsDark();
    const prevBg = bgTexRef.current;
    const nextBg = makeBackdrop(dark);
    sceneRef.current.background = nextBg;
    bgTexRef.current = nextBg;
    prevBg?.dispose();
    if (plateMatRef.current) {
      const old = plateMatRef.current.map;
      plateMatRef.current.map = makePeiTexture(dark);
      plateMatRef.current.color.set(0xffffff);
      plateMatRef.current.needsUpdate = true;
      old?.dispose();
    }
    if (lightsRef.current) {
      lightsRef.current.hemi.intensity = dark ? 0.9 : 1.15;
      lightsRef.current.key.intensity = dark ? 4.2 : 5.6;
      lightsRef.current.fill.intensity = dark ? 1.1 : 1.5;
    }
    if (volRef.current) {
      const mat = volRef.current.material as THREE.LineBasicMaterial;
      mat.color.set(dark ? 0x6ea8ff : 0x6aa8f0);
      mat.opacity = 0.32;
    }
  }, [theme]);

  return <div className="plate-viewer" ref={mountRef} data-testid="plate-viewer" />;

}

export async function capturePlateThumbnail(): Promise<Uint8Array | undefined> {
  const el = document.querySelector('.plate-viewer') as
    | (HTMLDivElement & { __capture?: () => Promise<Uint8Array | undefined> })
    | null;
  return el?.__capture?.();
}
